import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as adminService from '@/services/adminService';
import Modal from '@/components/ui/Modal';
import { resolveAssetUrl } from '@/utils/assets';

const categories = [
  { value: 'historia',     label: 'História',     color: '#D4AF37' }, // dourado
  { value: 'cultura',      label: 'Cultura',      color: '#E879F9' }, // fúcsia
  { value: 'gastronomia',  label: 'Gastronomia',  color: '#EF4444' }, // vermelho
  { value: 'natureza',     label: 'Natureza',     color: '#22C55E' }, // verde
  { value: 'comercio',     label: 'Comércio',     color: '#A78BFA' }, // violeta
  { value: 'aventura',     label: 'Aventura',     color: '#38BDF8' }, // azul-céu
  { value: 'desporto',     label: 'Desporto',     color: '#FB923C' }, // laranja
  { value: 'curiosidades', label: 'Curiosidades', color: '#10B981' }, // esmeralda
];

const emptyForm = {
  name: '',
  description: '',
  category: 'cultura',
  cityId: '',
  zoneId: '',
  lat: '',
  lng: '',
  coinReward: 50,
  xpReward: 30,
  collectionRadius: 100,
  difficulty: 2,
  isSpecial: false,
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';
const toNumber = (value) => Number(String(value).replace(',', '.'));

const FREE_MAP_STYLE = {
  version: 8,
  sources: {
    cartoDark: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
    },
  },
  layers: [{ id: 'carto-dark', type: 'raster', source: 'cartoDark' }],
};

const metersToPixels = (meters, lat, zoom) => {
  const earthCircumference = 40075017;
  return meters * (256 / earthCircumference) * Math.pow(2, zoom) / Math.cos((lat * Math.PI) / 180);
};

function RadiusMapPreview({ lat, lng, radius }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const previewZoom = 17;

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const parsedRadius = parseInt(radius, 10);
  const hasCoords = !Number.isNaN(parsedLat) && !Number.isNaN(parsedLng);
  const safeRadius = Number.isNaN(parsedRadius) ? 100 : parsedRadius;
  const circleSize = hasCoords ? Math.round(metersToPixels(safeRadius, parsedLat, previewZoom) * 2) : 0;

  useEffect(() => {
    if (!hasCoords || !containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: FREE_MAP_STYLE,
      center: [parsedLng, parsedLat],
      zoom: previewZoom,
      attributionControl: false,
      interactive: false,
    });

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [hasCoords]);

  useEffect(() => {
    if (!hasCoords || !mapRef.current) return;
    mapRef.current.easeTo({ center: [parsedLng, parsedLat], zoom: previewZoom, duration: 250 });
    mapRef.current.resize();
  }, [hasCoords, parsedLat, parsedLng]);

  if (!hasCoords) {
    return (
      <div className="grid h-48 place-items-center rounded-lg border border-fg/10 bg-black/25 text-sm text-fg/40">
        Insere latitude e longitude para ver o raio no mapa.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-accent/30">
      <div className="flex items-center justify-between bg-black/40 px-3 py-1.5">
        <span className="text-xs font-bold uppercase tracking-[0.16em] text-accent/60">Raio de recolha</span>
        <span className="text-xs font-bold text-fg/60">{safeRadius}m</span>
      </div>
      <div className="relative h-[220px] w-full bg-[#0e0e1f]">
        <div ref={containerRef} className="absolute inset-0" />
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border-2 border-accent bg-accent/15"
          style={{ width: circleSize, height: circleSize, transform: 'translate(-50%, -50%)' }}
        />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-edge bg-energy shadow-[0_0_18px_rgba(255,111,60,0.9)]" />
      </div>
    </div>
  );

}

export default function AdminPOIsPage() {
  const [pois, setPois] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showRadius, setShowRadius] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [filterCity, setFilterCity] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const aiTextRef = useRef(null);

  const cityById = useMemo(() => new Map(cities.map((city) => [city.id, city])), [cities]);
  const zoneById = useMemo(() => new Map(zones.map((zone) => [zone.id, zone])), [zones]);
  const filteredZones = zones.filter((zone) => !form.cityId || zone.cityId === form.cityId);

  const filterZonesForCity = useMemo(
    () => zones.filter((zone) => !filterCity || zone.cityId === filterCity),
    [zones, filterCity],
  );

  const visiblePois = useMemo(() => {
    return pois.filter((poi) => {
      if (filterCity && poi.cityId !== filterCity) return false;
      if (filterZone && poi.zoneId !== filterZone) return false;
      if (filterCategory && poi.category !== filterCategory) return false;
      if (filterStatus === 'active' && !poi.isActive) return false;
      if (filterStatus === 'inactive' && poi.isActive) return false;
      return true;
    });
  }, [pois, filterCity, filterZone, filterCategory, filterStatus]);

  const hasActiveFilters = filterCity || filterZone || filterCategory || filterStatus;

  const clearFilters = () => {
    setFilterCity('');
    setFilterZone('');
    setFilterCategory('');
    setFilterStatus('');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [poisRes, citiesRes, zonesRes] = await Promise.all([
        adminService.pois.list({ limit: 100 }),
        adminService.cities.list({ limit: 100 }),
        adminService.zones.list({ limit: 100 }),
      ]);

      const nextCities = citiesRes.data.data || [];
      const nextZones = zonesRes.data.data || [];
      setPois(poisRes.data.data || []);
      setCities(nextCities);
      setZones(nextZones);
      setForm((current) => ({
        ...current,
        cityId: current.cityId || nextCities[0]?.id || '',
        zoneId: current.zoneId || nextZones[0]?.id || '',
      }));
      setError('');
    } catch {
      setError('Não foi possível carregar os POIs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setFilterZone('');
  }, [filterCity]);

  const updateForm = (patch) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      if (patch.cityId && !zones.some((zone) => zone.cityId === patch.cityId && zone.id === next.zoneId)) {
        next.zoneId = zones.find((zone) => zone.cityId === patch.cityId)?.id || '';
      }
      return next;
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      cityId: cities[0]?.id || '',
      zoneId: zones.find((zone) => zone.cityId === cities[0]?.id)?.id || zones[0]?.id || '',
    });
    setNotice('');
    setError('');
    setImageFile(null);
    setImagePreview('');
    setAiText('');
    setAiError('');
  };

  const applyAiSuggestion = async () => {
    const text = aiText.trim();
    if (!text) return;
    setAiLoading(true);
    setAiError('');
    try {
      const res = await adminService.aiSuggestPOI(text);
      const s = res.data.data || {};
      updateForm({
        ...(s.name && { name: s.name }),
        ...(s.description && { description: s.description }),
        ...(s.category && { category: s.category }),
        ...(s.lat != null && { lat: String(s.lat) }),
        ...(s.lng != null && { lng: String(s.lng) }),
        ...(s.coinReward != null && { coinReward: s.coinReward }),
        ...(s.xpReward != null && { xpReward: s.xpReward }),
        ...(s.collectionRadius != null && { collectionRadius: s.collectionRadius }),
      });
    } catch (err) {
      setAiError(err.response?.data?.message || 'Erro ao contactar a IA. Tenta novamente.');
    } finally {
      setAiLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const editPOI = (poi) => {
    setEditingId(poi.id);
    setForm({
      name: poi.name || '',
      description: poi.description || '',
      category: poi.category || 'cultura',
      cityId: poi.cityId || '',
      zoneId: poi.zoneId || '',
      lat: poi.lat || '',
      lng: poi.lng || '',
      coinReward: poi.coinReward ?? 50,
      xpReward: poi.xpReward ?? 30,
      collectionRadius: poi.collectionRadius ?? 100,
      difficulty: poi.difficulty ?? 2,
      isSpecial: Boolean(poi.isSpecial),
      isActive: Boolean(poi.isActive),
    });
    setImageFile(null);
    setImagePreview(resolveAssetUrl(poi.imageUrl));
    setNotice('');
    setError('');
    setModalOpen(true);
  };

  const savePOI = async (event) => {
    event.preventDefault();
    setSaving(true);
    setNotice('');
    setError('');

    const payload = {
      ...form,
      lat: toNumber(form.lat),
      lng: toNumber(form.lng),
      coinReward: toNumber(form.coinReward),
      xpReward: toNumber(form.xpReward),
      collectionRadius: toNumber(form.collectionRadius),
    };

    try {
      let savedPOI;
      if (editingId) {
        const res = await adminService.pois.update(editingId, payload);
        savedPOI = res.data.data;
        setNotice('POI atualizado com sucesso.');
      } else {
        const res = await adminService.pois.create(payload);
        savedPOI = res.data.data;
        setNotice('POI criado com sucesso.');
      }

      if (imageFile && savedPOI?.id) {
        await adminService.uploadPOIImage(savedPOI.id, imageFile);
      }

      await loadData();
      resetForm();
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar o POI.');
    } finally {
      setSaving(false);
    }
  };

  const selectImage = (file) => {
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : '');
  };

  const toggleActive = async (poi) => {
    try {
      await adminService.pois.update(poi.id, { isActive: !poi.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado do POI.');
    }
  };

  const removePOI = async (poi) => {
    if (!window.confirm(`Remover o POI "${poi.name}"?`)) return;

    try {
      await adminService.pois.remove(poi.id);
      await loadData();
      if (editingId === poi.id) resetForm();
    } catch {
      setError('Não foi possível remover o POI.');
    }
  };

  const resetVisit = async (poi) => {
    if (!window.confirm(`Reiniciar visitas ao POI "${poi.name}"?\nTodos os jogadores poderão visitá-lo novamente como primeira visita.`)) return;
    setResettingId(poi.id);
    try {
      await adminService.resetPOIVisit(poi.id);
      setNotice(`Visitas ao POI "${poi.name}" reiniciadas.`);
    } catch {
      setError('Não foi possível reiniciar as visitas.');
    } finally {
      setResettingId(null);
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Conteúdo do mapa</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de POIs</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Cria e mantém os locais visitáveis, recompensas e raio de coleta do jogo.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20"
        >
          Novo POI
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <div className="mt-6">
        <section className="rounded-lg border border-fg/10 bg-fg/[0.04]">
          <div className="border-b border-fg/10 p-4">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-serif text-xl font-black text-accent">Locais registados</h2>
              <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">
                {loading ? 'A carregar...' : hasActiveFilters ? `${visiblePois.length} / ${pois.length} POI(s)` : `${pois.length} POI(s)`}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <select
                className="rounded-lg border border-fg/10 bg-black/25 px-3 py-1.5 text-xs font-bold text-fg/70 outline-none focus:border-accent"
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
              >
                <option value="">Todas as cidades</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>{city.name}</option>
                ))}
              </select>

              <select
                className="rounded-lg border border-fg/10 bg-black/25 px-3 py-1.5 text-xs font-bold text-fg/70 outline-none focus:border-accent disabled:opacity-40"
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                disabled={!filterCity}
              >
                <option value="">Todas as zonas</option>
                {filterZonesForCity.map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </select>

              <select
                className="rounded-lg border border-fg/10 bg-black/25 px-3 py-1.5 text-xs font-bold text-fg/70 outline-none focus:border-accent"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">Todas as categorias</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>

              <select
                className="rounded-lg border border-fg/10 bg-black/25 px-3 py-1.5 text-xs font-bold text-fg/70 outline-none focus:border-accent"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Todos os estados</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-lg border border-fg/15 px-3 py-1.5 text-xs font-bold text-fg/45 hover:border-fg/30 hover:text-fg/70"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
                <tr>
                  <th className="px-4 py-3">Foto</th>
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Zona</th>
                  <th className="px-4 py-3">Categoria</th>
                  <th className="px-4 py-3">Recompensa</th>
                  <th className="px-4 py-3">Raio</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {!loading && visiblePois.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-fg/45" colSpan="8">
                      {hasActiveFilters ? 'Nenhum POI corresponde aos filtros selecionados.' : 'Nenhum POI registado.'}
                    </td>
                  </tr>
                )}
                {visiblePois.map((poi) => (
                  <tr key={poi.id} className="align-top">
                    <td className="px-4 py-3">
                      <div className="h-14 w-20 overflow-hidden rounded-lg border border-fg/10 bg-black/25">
                        {poi.imageUrl ? (
                          <img src={resolveAssetUrl(poi.imageUrl)} alt={poi.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="grid h-full place-items-center text-xs text-fg/30">Sem foto</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-fg">{poi.name}</div>
                      <div className="mt-1 text-xs text-fg/40">{cityById.get(poi.cityId)?.name || 'Cidade não definida'}</div>
                    </td>
                    <td className="px-4 py-3 text-fg/60">{zoneById.get(poi.zoneId)?.name || '-'}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const cat = categories.find((c) => c.value === poi.category);
                        return (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                            style={{ background: cat ? `${cat.color}22` : 'rgba(255,255,255,0.08)', color: cat?.color || 'rgba(255,255,255,0.5)', border: `1px solid ${cat ? cat.color + '44' : 'rgba(255,255,255,0.1)'}` }}
                          >
                            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: cat?.color || 'rgba(255,255,255,0.4)' }} />
                            {cat?.label || poi.category}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-fg/60">{poi.coinReward} moedas · {poi.xpReward} XP</td>
                    <td className="px-4 py-3 text-fg/60">{poi.collectionRadius}m</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(poi)}
                        className={`rounded-full border px-3 py-1 text-xs font-bold ${poi.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}
                      >
                        {poi.isActive ? 'Ativo' : 'Inativo'}
                      </button>
                      {poi.isSpecial && <div className="mt-2 text-xs font-bold text-accent">Especial</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => editPOI(poi)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => resetVisit(poi)}
                        disabled={resettingId === poi.id}
                        className="mr-2 rounded-lg border border-accent/30 px-3 py-1.5 text-xs font-bold text-accent hover:bg-accent/10 disabled:opacity-50"
                        title="Apaga o histórico de visitas para que todos possam visitar novamente"
                      >
                        {resettingId === poi.id ? '...' : 'Reiniciar'}
                      </button>
                      <button type="button" onClick={() => removePOI(poi)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Mapa</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar POI' : 'Novo POI'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">
              Fechar
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-ocean-light/20 bg-ocean-light/5 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-ocean-light/70">Sugestão com IA</p>
            <textarea
              ref={aiTextRef}
              className="w-full resize-none rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-ocean-light placeholder:text-fg/30"
              rows={3}
              placeholder="Descreve o local em texto livre... ex: &quot;Torre medieval do século XIV no centro histórico de Aveiro, com vista panorâmica.&quot;"
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) applyAiSuggestion(); }}
            />
            {aiError && <p className="mt-1 text-xs font-bold text-energy">{aiError}</p>}
            <button
              type="button"
              onClick={applyAiSuggestion}
              disabled={aiLoading || !aiText.trim()}
              className="mt-2 rounded-lg border border-ocean-light/40 bg-ocean-light/10 px-4 py-2 text-xs font-bold text-ocean-light hover:bg-ocean-light/20 disabled:opacity-50"
            >
              {aiLoading ? 'A gerar sugestão...' : 'Preencher com IA'}
            </button>
            <p className="mt-1 text-xs text-fg/30">Ctrl+Enter para enviar · Os campos preenchidos pela IA podem ser editados antes de guardar.</p>
          </div>

          <form className="mt-5 grid gap-3" onSubmit={savePOI}>
            <label className="grid gap-1">
              <span className={labelClass}>Nome</span>
              <input className={fieldClass} value={form.name} onChange={(e) => updateForm({ name: e.target.value })} required />
            </label>

            <label className="grid gap-1">
              <span className={labelClass}>Descrição</span>
              <textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => updateForm({ description: e.target.value })} />
            </label>

            <div className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 sm:grid-cols-[180px_1fr]">
              <div className="h-36 overflow-hidden rounded-lg border border-fg/10 bg-black/25">
                {imagePreview ? (
                  <img src={imagePreview} alt="Pré-visualização do POI" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center px-4 text-center text-sm text-fg/35">
                    Foto do local
                  </div>
                )}
              </div>
              <div className="grid content-center gap-2">
                <span className={labelClass}>Foto do POI</span>
                <input
                  className="block w-full text-sm text-fg/70 file:mr-3 file:rounded-lg file:border-0 file:bg-accent file:px-3 file:py-2 file:font-bold file:text-fg"
                  type="file"
                  accept="image/*"
                  onChange={(e) => selectImage(e.target.files?.[0])}
                />
                <p className="text-xs text-fg/40">
                  Usa uma imagem real e reconhecível do local. Formatos recomendados: JPG, PNG ou WebP até 5 MB.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <label className="grid gap-1">
                <span className={labelClass}>Cidade</span>
                <select className={fieldClass} value={form.cityId} onChange={(e) => updateForm({ cityId: e.target.value })} required>
                  {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                </select>
              </label>

              <label className="grid gap-1">
                <span className={labelClass}>Zona</span>
                <select className={fieldClass} value={form.zoneId} onChange={(e) => updateForm({ zoneId: e.target.value })} required>
                  {filteredZones.map((zone) => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
                </select>
              </label>
            </div>

            <div className="grid gap-1">
              <span className={labelClass}>Categoria</span>
              <div className="grid grid-cols-4 gap-1.5">
                {categories.map((cat) => {
                  const active = form.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => updateForm({ category: cat.value })}
                      style={{
                        borderColor: active ? cat.color : 'rgba(255,255,255,0.1)',
                        background: active ? `${cat.color}22` : 'rgba(0,0,0,0.2)',
                        color: active ? cat.color : 'rgba(255,255,255,0.45)',
                        boxShadow: active ? `0 0 0 1px ${cat.color}55, 0 2px 8px ${cat.color}33` : 'none',
                      }}
                      className="flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-center transition-all"
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ background: cat.color, opacity: active ? 1 : 0.4 }}
                      />
                      <span className="text-[10px] font-bold leading-tight">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1">
                <span className={labelClass}>Latitude</span>
                <input className={fieldClass} type="number" step="0.0000001" value={form.lat} onChange={(e) => updateForm({ lat: e.target.value })} required />
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>Longitude</span>
                <input className={fieldClass} type="number" step="0.0000001" value={form.lng} onChange={(e) => updateForm({ lng: e.target.value })} required />
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="grid gap-1">
                <span className={labelClass}>Moedas</span>
                <input className={fieldClass} type="number" min="0" value={form.coinReward} onChange={(e) => updateForm({ coinReward: e.target.value })} />
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>XP</span>
                <input className={fieldClass} type="number" min="0" value={form.xpReward} onChange={(e) => updateForm({ xpReward: e.target.value })} />
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>Raio</span>
                <input className={fieldClass} type="number" min="10" value={form.collectionRadius} onChange={(e) => updateForm({ collectionRadius: e.target.value })} />
              </label>
            </div>

            <div className="grid gap-1">
              <span className={labelClass}>Dificuldade</span>
              <div className="grid grid-cols-3 gap-2">
                {[{ value: 1, label: 'Fácil', stars: '★☆☆' }, { value: 2, label: 'Média', stars: '★★☆' }, { value: 3, label: 'Difícil', stars: '★★★' }].map((d) => {
                  const active = Number(form.difficulty) === d.value;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => updateForm({ difficulty: d.value })}
                      className={`rounded-lg border px-3 py-2 text-xs font-bold transition-all ${active ? 'border-accent/60 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/45 hover:border-fg/20 hover:text-fg/70'}`}
                    >
                      <div>{d.stars}</div>
                      <div className="mt-0.5">{d.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 rounded-lg border border-fg/10 bg-black/15 p-3">
              <label className="flex items-center gap-2 text-sm font-bold text-fg/70">
                <input type="checkbox" checked={form.isActive} onChange={(e) => updateForm({ isActive: e.target.checked })} />
                Ativo
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-fg/70">
                <input type="checkbox" checked={form.isSpecial} onChange={(e) => updateForm({ isSpecial: e.target.checked })} />
                Especial
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-fg/70">
                <input type="checkbox" checked={showRadius} onChange={(e) => setShowRadius(e.target.checked)} />
                Exibir raio
              </label>
            </div>

            {showRadius && <RadiusMapPreview lat={form.lat} lng={form.lng} radius={form.collectionRadius} />}

            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">
              {saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar POI'}
            </button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
